import { motion } from "framer-motion";
import {
  BookOpen,
  ChevronRight,
  Star,
  Users,
  Award,
  Flame,
  Globe,
  Zap,
  Search,
  Loader2,
  ChevronDown,
  CheckCircle2,
  Clock,
  HelpCircle,
  TrendingUp,
  ShieldCheck,
  Smartphone,
  Phone,
  Mail,
  MapPin,
  Sparkles,
  Package,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { KCard } from "@/components/KCard";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyEnrollments, getPublicCourses, enrollCourseWithBkash, formatCourseLevels } from "@/api/lms/courses";
import { getMockTests, getMockTestBundles, type MockTest, type MockTestBundle } from "@/api/lms/mock-tests";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { useMemo, useState, useEffect } from "react";
import { PublicHeader } from "@/components/PublicHeader";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const fadeUp = {
  initial: { opacity: 0, y: 25 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } },
};

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const startHref =
    user?.role === "admin" ? "/admin" : user?.role === "teacher" ? "/teacher" : "/dashboard";

  // Course filter states
  const [courseSearchInput, setCourseSearchInput] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [courseLevel, setCourseLevel] = useState("");
  const [courseType, setCourseType] = useState("");
  const [courseLimit, setCourseLimit] = useState(6);

  // FAQ accordion state
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCourseSearch(courseSearchInput);
      setCourseLimit(6);
    }, 300);
    return () => clearTimeout(timer);
  }, [courseSearchInput]);

  // Query Courses
  const coursesQuery = useQuery({
    queryKey: ["publicCourses", courseLimit, courseSearch, courseLevel, courseType],
    queryFn: () => getPublicCourses({ page: 1, limit: courseLimit, search: courseSearch, level: courseLevel, type: courseType }),
  });

  // Query Mock Tests for landing page (Popular Mock Tests)
  const mockTestsQuery = useQuery({
    queryKey: ["landingMockTests"],
    queryFn: () => getMockTests({ page: 1, limit: 6 }),
  });

  const bundlesQuery = useQuery({
    queryKey: ["landingMockTestBundles"],
    queryFn: () => getMockTestBundles(true),
  });

  const myEnrollmentsQuery = useQuery({
    queryKey: ["myEnrollments", user?.id],
    queryFn: getMyEnrollments,
    enabled: Boolean(user && (user.role === "student" || user.role === "admin")),
  });

  const enrolledCourseIds = useMemo(() => {
    return new Set(myEnrollmentsQuery.data?.map((e) => e.course?.id).filter(Boolean) ?? []);
  }, [myEnrollmentsQuery.data]);

  const enrollMutation = useMutation({
    mutationFn: (courseId: string) => enrollCourseWithBkash(courseId),
    onSuccess: (data) => {
      if (data.bkashURL) {
        toast.success("Redirecting to bKash payment...");
        window.location.href = data.bkashURL;
        return;
      }
      toast.success("Enrolled successfully! Welcome to the course.");
    },
    onError: (err) => {
      toast.error((err as any)?.message || "Enrollment failed");
    },
  });

  const showCourses = useMemo(() => coursesQuery.data?.items ?? [], [coursesQuery.data?.items]);
  const popularMockTests = useMemo(() => mockTestsQuery.data?.items ?? [], [mockTestsQuery.data?.items]);
  const featuredBundles = useMemo(() => bundlesQuery.data ?? [], [bundlesQuery.data]);

  const faqs = [
    {
      q: "What is the difference between JLPT and JFT-Basic?",
      a: "The Japanese-Language Proficiency Test (JLPT) is the traditional benchmark with 5 levels (N5 being easiest, N1 hardest) widely used for academic and professional tracks. The JFT-Basic (Japanese Foundation Test) assesses communicative language competence required for the 'Specified Skilled Worker' (SSW) visa and corresponds to the CEFR A2 level.",
    },
    {
      q: "How does the Mock Test CBT Simulator work?",
      a: "Our mock exam simulator replicates the exact computer-based testing interface of the real JFT-Basic/JLPT. It has strict timing per section (Vocabulary, Grammar, Reading, Listening), locked progression (no going back once a section ends), and single-playback listening audio to simulate actual exam stress.",
    },
    {
      q: "Can I try a Mock Test for free?",
      a: "Yes! We offer free introductory mock tests. Premium exams are available for purchase individually or in highly discounted JLPT/JFT bundles.",
    },
    {
      q: "How long do I keep access to purchased courses and tests?",
      a: "Courses offer lifetime access including all future updates. Mock tests generally provide a specific number of timed attempts (e.g. 3 attempts per test) to align with structured exam preparation.",
    },
    {
      q: "Does MEHUL provide certificates?",
      a: "Yes. Upon successful completion of any of our structured JLPT preparatory courses, you will receive a digital certificate of completion containing a verification hash.",
    },
  ];

  return (
    <div className="min-h-screen bg-background landing-typography text-foreground">
      {/* Navigation Header */}
      <PublicHeader />

      {/* SECTION 1: Hero Section */}
      <section className="relative overflow-hidden pt-20 md:pt-32 pb-24 md:pb-36 bg-gradient-to-b from-primary/5 via-background to-background">
        {/* Background visual accents */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl opacity-50 pointer-events-none" />
        <div className="absolute top-10 right-10 w-72 h-72 bg-secondary/10 rounded-full blur-3xl opacity-30 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div className="lg:col-span-7 text-left space-y-6" {...fadeUp}>
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold tracking-wider text-primary uppercase bg-primary/10 rounded-full select-none">
              <Sparkles size={13} className="text-primary animate-pulse" />
              Interactive EdTech Platform
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-foreground leading-[1.15]">
              Learn Japanese.<br />
              Practice Real Exams.<br />
              <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Pass JLPT & JFT-Basic.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              MEHUL is the ultimate Japanese preparation portal. Join structured academic courses, build vocabulary with adaptive tools, and sit real timed mock examinations with detailed grading analytics.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a href="#courses">
                <Button size="xl" className="bg-primary text-primary-foreground hover:bg-primary/95 shadow-md shadow-primary/20 rounded-xl px-8 h-12 text-sm font-semibold tracking-wide">
                  Browse Courses
                </Button>
              </a>
              <a href="#mock-tests">
                <Button size="xl" variant="outline" className="border-border hover:bg-muted text-foreground rounded-xl px-8 h-12 text-sm font-semibold tracking-wide">
                  Explore Mock Tests
                </Button>
              </a>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative mx-auto max-w-[400px] lg:max-w-none aspect-square bg-gradient-to-tr from-primary/10 to-secondary/10 rounded-3xl p-6 border border-border/40 backdrop-blur-md shadow-2xl flex flex-col justify-between">
              <div className="flex items-center justify-between border-b border-border/50 pb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-rose-500" />
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                </div>
                <span className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground font-semibold">CBT Simulator v2.0</span>
              </div>

              {/* Graphic Simulator mock */}
              <div className="flex-1 flex flex-col justify-center space-y-4 my-6 select-none font-sans">
                <div className="bg-background border border-border/80 p-4 rounded-xl shadow-sm">
                  <div className="flex justify-between items-center text-[10px] text-muted-foreground mb-1">
                    <span>JLPT N5 MOCK TEST #1</span>
                    <span className="font-mono text-primary font-semibold">45:00 min left</span>
                  </div>
                  <h4 className="text-sm font-bold text-foreground leading-relaxed">
                    問題① つぎの ことばの かきかたとして 正しいものを 選びなさい。
                  </h4>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {["① ともだち", "② どもだち", "③ ともたち", "④ どもたち"].map((opt, i) => (
                      <div key={i} className={cn(
                        "p-2 text-xs border rounded-lg text-left transition-all",
                        i === 0 ? "border-primary bg-primary/5 text-primary font-semibold" : "border-border/80 bg-background text-muted-foreground"
                      )}>
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <span className="text-[10px] px-2 py-1 rounded bg-secondary text-muted-foreground border">Vocabulary</span>
                  <span className="text-[10px] px-2 py-1 rounded bg-secondary text-muted-foreground border">Grammar</span>
                  <span className="text-[10px] px-2 py-1 rounded bg-primary/10 text-primary font-semibold border border-primary/20">Listening</span>
                </div>
              </div>

              <div className="border-t border-border/50 pt-4 flex items-center justify-between">
                <span className="text-xs text-muted-foreground font-medium">Ready to take the challenge?</span>
                <Link to={user ? "/mock-tests" : "/login"}>
                  <Button size="sm" className="gap-1 rounded-lg text-[11px] h-8 font-bold">
                    Start Test <ChevronRight size={12} />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats bar */}
        <div className="max-w-7xl mx-auto px-6 md:px-8 mt-20 md:mt-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 bg-muted/30 border border-border/60 rounded-3xl backdrop-blur-md">
            {[
              { value: "15K+", label: "Registered Students", icon: Users },
              { value: "40+", label: "Expert Mock Tests", icon: Award },
              { value: "97.4%", label: "JLPT Exam Pass Rate", icon: TrendingUp },
              { value: "24/7", label: "CBT Simulator Access", icon: Clock },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center space-y-1">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                  <s.icon className="text-primary" size={18} />
                </div>
                <p className="text-3xl font-extrabold text-foreground tracking-tight">{s.value}</p>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2: Featured Courses Section */}
      <section id="courses" className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28 border-t border-border/30">
        <div className="flex flex-col gap-6 mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="text-xs font-bold text-primary uppercase tracking-widest block mb-2">Academic Curriculum</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">Structured Japanese Courses</h2>
              <p className="text-muted-foreground mt-1 max-w-xl">Learn vocabulary, grammar, kanji, and comprehension from scratch with video lessons and progress trackers.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={15} />
                <Input
                  type="text"
                  placeholder="Search course title..."
                  value={courseSearchInput}
                  onChange={(e) => setCourseSearchInput(e.target.value)}
                  className="pl-9 bg-background border-border focus-visible:ring-primary rounded-xl h-10 w-full text-xs"
                />
              </div>
              <select
                className="h-10 px-4 rounded-xl border border-border bg-background text-xs text-foreground focus:ring-1 focus:ring-primary focus:border-primary outline-none min-w-[130px] cursor-pointer appearance-none"
                value={courseType}
                onChange={(e) => {
                  setCourseType(e.target.value);
                  setCourseLimit(6);
                }}
              >
                <option value="">All Methods</option>
                <option value="LIVE">Live Classes</option>
                <option value="RECORDED">Recorded Lessons</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 border-b border-border/50 pb-4">
            {[
              { label: "All Levels", value: "" },
              { label: "N5 Level", value: "N5" },
              { label: "N4 Level", value: "N4" },
              { label: "N3 Level", value: "N3" },
              { label: "N2 Level", value: "N2" },
              { label: "N1 Level", value: "N1" },
              { label: "JFT A2 Basic", value: "JFT A2" },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => {
                  setCourseLevel(tab.value);
                  setCourseLimit(6);
                }}
                className={cn(
                  "px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all duration-200 border",
                  courseLevel === tab.value
                    ? "bg-primary border-primary text-primary-foreground shadow-sm shadow-primary/10"
                    : "bg-background border-border text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                {tab.label}
              </button>
            ))}

            {(courseSearchInput || courseLevel || courseType) && (
              <Button
                variant="ghost"
                onClick={() => {
                  setCourseSearchInput("");
                  setCourseSearch("");
                  setCourseLevel("");
                  setCourseType("");
                  setCourseLimit(6);
                }}
                className="text-xs text-muted-foreground hover:text-destructive hover:bg-transparent font-semibold px-2 py-1 h-auto ml-2"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={stagger}
          initial="initial"
          animate="animate"
        >
          {coursesQuery.isPending ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <motion.div key={idx} variants={fadeUp}>
                <KCard className="p-6 h-full space-y-4">
                  <Skeleton className="h-44 w-full rounded-xl" />
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <div className="flex gap-3 justify-between pt-2">
                    <Skeleton className="h-9 w-24 rounded-lg" />
                    <Skeleton className="h-9 w-24 rounded-lg" />
                  </div>
                </KCard>
              </motion.div>
            ))
          ) : coursesQuery.isError ? (
            <div className="col-span-full py-12 text-center text-sm text-destructive border border-dashed rounded-2xl bg-destructive/5 font-medium">
              Failed to retrieve courses from the database. Please try reloading.
            </div>
          ) : showCourses.length === 0 ? (
            <div className="col-span-full py-16 text-center text-sm text-muted-foreground border border-dashed rounded-2xl bg-muted/10 font-medium">
              No matching courses found. Try adjusting your filter tags or search keyword.
            </div>
          ) : (
            showCourses.map((course) => (
              <motion.div key={course.id} variants={fadeUp}>
                <KCard 
                  hoverable 
                  className="p-5 h-full flex flex-col border border-border/80"
                  onClick={() => navigate(`/course?courseId=${course.id}`)}
                >
                  <div className="w-full aspect-[16/9] rounded-xl bg-primary/5 overflow-hidden flex items-center justify-center relative border border-border/30">
                    {course.thumbnail ? (
                      <img
                        src={course.thumbnail}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <BookOpen className="text-primary/60" size={32} />
                        <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wide">Mehul LMS</span>
                      </div>
                    )}
                    <span className="absolute top-3 right-3 text-xs font-extrabold rounded-lg bg-background/90 text-foreground border px-2.5 py-1 backdrop-blur-xs font-mono shadow-xs">
                      ৳{course.price ?? "0"}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1 mt-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                        {formatCourseLevels(course)}
                      </span>
                      <span className="text-[10px] font-semibold text-muted-foreground">
                        {course.type === "LIVE" ? "Live Interactive" : "Self-paced Recorded"}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-foreground leading-snug line-clamp-2 hover:text-primary transition-colors">
                      {course.title}
                    </h3>

                    <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                      {course.description ?? "Access structured chapters, interactive assignments, grammar maps, and full study materials."}
                    </p>
                  </div>

                </KCard>
              </motion.div>
            ))
          )}
        </motion.div>

        {coursesQuery.data && showCourses.length < coursesQuery.data.total && (
          <div className="flex justify-center mt-12">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setCourseLimit((prev) => prev + 3)}
              disabled={coursesQuery.isFetching}
              className="px-6 py-5 rounded-xl border hover:bg-muted font-semibold tracking-wide text-xs gap-1.5 shadow-sm h-10"
            >
              {coursesQuery.isFetching ? (
                <>
                  <Loader2 className="animate-spin size-3" /> Loading...
                </>
              ) : (
                "Load More Courses"
              )}
            </Button>
          </div>
        )}
      </section>

      {/* SECTION 3: Featured Mock Tests ("Popular Mock Tests") */}
      <section id="mock-tests" className="bg-muted/15 border-t border-b border-border/30 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-bold text-primary uppercase tracking-widest block mb-2 font-sans">Exam Practice</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">Popular Mock Tests</h2>
              <p className="text-muted-foreground mt-1 max-w-xl">Sharpen your examination skills with official-format test papers using our high-fidelity simulator.</p>
            </div>
            <Link to="/mock-tests">
              <Button variant="outline" className="border-border hover:bg-background/80 rounded-xl h-10 text-xs font-semibold tracking-wide gap-1">
                View All Mock Tests <ChevronRight size={14} />
              </Button>
            </Link>
          </div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={stagger}
            initial="initial"
            animate="animate"
          >
            {mockTestsQuery.isPending ? (
              Array.from({ length: 3 }).map((_, idx) => (
                <motion.div key={idx} variants={fadeUp}>
                  <KCard className="p-6 h-full space-y-4">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <div className="space-y-2 pt-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                    <Skeleton className="h-10 w-full rounded-xl pt-2" />
                  </KCard>
                </motion.div>
              ))
            ) : mockTestsQuery.isError ? (
              <div className="col-span-full py-12 text-center text-sm text-destructive border border-dashed rounded-2xl bg-destructive/5 font-medium">
                Failed to load mock tests.
              </div>
            ) : popularMockTests.length === 0 ? (
              <div className="col-span-full py-16 text-center text-sm text-muted-foreground border border-dashed rounded-2xl bg-muted/10 font-medium">
                No mock tests published yet.
              </div>
            ) : (
              popularMockTests.slice(0, 6).map((test) => {
                const isFree = !test.price || Number(test.price) === 0;
                return (
                  <motion.div key={test.id} variants={fadeUp}>
                    <KCard 
                      hoverable 
                      className="p-6 h-full flex flex-col border border-border/80 bg-background relative overflow-hidden"
                      onClick={() => navigate(`/mock-tests?testId=${test.id}`)}
                    >
                      {/* Flag accent tag */}
                      <span className={cn(
                        "absolute top-4 right-4 text-[9px] font-extrabold uppercase px-2 py-0.5 rounded border tracking-wider",
                        test.examType === "JFT_BASIC"
                          ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                          : "bg-rose-500/10 text-rose-600 border-rose-500/20"
                      )}>
                        {test.examType === "JFT_BASIC" ? "JFT-Basic" : "JLPT"}
                      </span>

                      <div className="flex-1 space-y-4">
                        <div className="space-y-1.5 pr-14">
                          <span className="text-[10px] font-extrabold tracking-widest text-primary uppercase">
                            LEVEL {test.level}
                          </span>
                          <h3 className="text-lg font-bold text-foreground leading-snug line-clamp-2">
                            {test.title || `${test.level} Mock Exam`}
                          </h3>
                        </div>

                        {/* Specs grid */}
                        <div className="grid grid-cols-2 gap-3 py-3 border-t border-b border-border/40 text-xs text-muted-foreground font-sans">
                          <div className="flex items-center gap-1.5">
                            <Clock size={14} className="text-primary/70 shrink-0" />
                            <span>{test.duration} Minutes</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <BookOpen size={14} className="text-primary/70 shrink-0" />
                            <span>{test.questionCount ?? 0} Questions</span>
                          </div>
                        </div>

                        {/* Pricing info */}
                        <div className="flex items-baseline gap-1.5 font-sans pt-1">
                          {isFree ? (
                            <span className="text-lg font-extrabold text-emerald-600">Free Practice</span>
                          ) : (
                            <>
                              <span className="text-xl font-black text-foreground">৳{test.price}</span>
                              <span className="text-[10px] text-muted-foreground uppercase font-bold">BDT</span>
                              {test.attemptLimit != null && (
                                <span className="text-[10px] text-muted-foreground ml-auto bg-muted px-2 py-0.5 rounded border font-semibold">
                                  {test.attemptLimit} attempt{test.attemptLimit > 1 ? "s" : ""}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                    </KCard>
                  </motion.div>
                );
              })
            )}
          </motion.div>
        </div>
      </section>

      {/* SECTION 3b: Mock Test Bundles — only shown when bundles exist */}
      {featuredBundles.length > 0 && (
        <section id="mock-test-bundles" className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28 border-t border-border/30">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-bold text-primary uppercase tracking-widest block mb-2 font-sans">Save More</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">Mock Test Bundles</h2>
              <p className="text-muted-foreground mt-1 max-w-xl">Grab several mock tests together in a discounted package and unlock them all at once.</p>
            </div>
            <Link to="/mock-test-bundles">
              <Button variant="outline" className="border-border hover:bg-background/80 rounded-xl h-10 text-xs font-semibold tracking-wide gap-1">
                View All Bundles <ChevronRight size={14} />
              </Button>
            </Link>
          </div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={stagger}
            initial="initial"
            animate="animate"
          >
            {featuredBundles.slice(0, 6).map((bundle: MockTestBundle) => {
              const saved = Number(bundle.originalPrice) - Number(bundle.discountedPrice);
              const savePct = Number(bundle.originalPrice) > 0
                ? Math.round((saved / Number(bundle.originalPrice)) * 100)
                : 0;
              return (
                <motion.div key={bundle.id} variants={fadeUp}>
                  <KCard
                    hoverable
                    className="p-6 h-full flex flex-col border border-border/80 bg-background relative overflow-hidden"
                    onClick={() => navigate("/mock-test-bundles")}
                  >
                    {savePct > 0 && (
                      <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[9px] font-extrabold uppercase px-2 py-0.5 rounded border tracking-wider bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                        <Tag size={10} /> Save {savePct}%
                      </span>
                    )}

                    <div className="flex-1 space-y-4">
                      <div className="space-y-1.5 pr-16">
                        <span className="inline-flex items-center gap-1 text-[10px] font-extrabold tracking-widest text-primary uppercase">
                          <Package size={12} /> Bundle
                        </span>
                        <h3 className="text-lg font-bold text-foreground leading-snug line-clamp-2">
                          {bundle.title}
                        </h3>
                      </div>

                      <div className="py-3 border-t border-b border-border/40 space-y-1.5">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          {bundle.items.length} test{bundle.items.length !== 1 ? "s" : ""} included
                        </div>
                        <ul className="space-y-1">
                          {bundle.items.slice(0, 3).map((item) => (
                            <li key={item.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                              <span className="truncate">{item.mockTest?.title || item.mockTest?.level || "Mock test"}</span>
                            </li>
                          ))}
                          {bundle.items.length > 3 && (
                            <li className="text-[10px] text-muted-foreground pl-5">+ {bundle.items.length - 3} more</li>
                          )}
                        </ul>
                      </div>

                      <div className="flex items-baseline gap-2 font-sans pt-1">
                        {saved > 0 && (
                          <span className="text-xs line-through text-muted-foreground">৳{Number(bundle.originalPrice).toLocaleString()}</span>
                        )}
                        <span className="text-xl font-black text-foreground">৳{Number(bundle.discountedPrice).toLocaleString()}</span>
                        <span className="text-[10px] text-muted-foreground uppercase font-bold">BDT</span>
                      </div>
                    </div>
                  </KCard>
                </motion.div>
              );
            })}
          </motion.div>
        </section>
      )}

      {/* SECTION 4: JLPT Preparation Section */}
      <section id="jlpt" className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="text-xs font-bold text-rose-500 uppercase tracking-widest block font-sans">Official Assessment Preparation</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight leading-tight">
              Japanese-Language Proficiency Test (JLPT) Practice
            </h2>
            <p className="text-muted-foreground leading-relaxed text-sm">
              The JLPT evaluates and certifies Japanese language proficiency for non-native speakers across 5 levels. Our mock exam papers are designed to emulate the exact layout, difficulty ratio, and pacing of the actual test sheets.
            </p>
            
            <div className="space-y-4.5 font-sans">
              {[
                { title: "N5 & N4 Levels", desc: "Basic Japanese. Evaluates understanding of classroom phrases, basic vocabulary, and grammar structures." },
                { title: "N3 Level", desc: "Bridge to advanced. Evaluates ability to comprehend conversation in daily situations at near-natural speed." },
                { title: "N2 & N1 Levels", desc: "Advanced proficiency. Evaluates complex reading, structural essays, and natural vocabulary speed." }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3">
                  <div className="w-5 h-5 rounded-full bg-rose-500/10 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 size={13} className="fill-rose-500/10" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">{item.title}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-2">
              <Link to="/mock-tests?examType=JLPT">
                <Button className="bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl text-xs h-10 px-6">
                  Browse JLPT Mock Exams
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 bg-gradient-to-br from-rose-500/5 to-rose-500/0 border border-rose-500/10 p-8 rounded-3xl relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-44 h-44 bg-rose-500/5 rounded-full blur-xl" />
            <h3 className="text-lg font-bold text-rose-700 dark:text-rose-400 mb-6 border-b border-rose-500/10 pb-3">
              JLPT 試験構造 / Exam Structure Breakdown
            </h3>
            
            <div className="space-y-4 font-sans text-xs">
              <div className="flex justify-between items-start gap-4">
                <div className="space-y-0.5">
                  <strong className="text-foreground block text-sm">言語知識 (文字・語彙・文法)</strong>
                  <span className="text-muted-foreground block text-[11px]">Language Knowledge (Vocabulary/Grammar)</span>
                </div>
                <span className="text-stone-700 dark:text-stone-300 font-bold bg-muted px-2.5 py-1 rounded border">25-60 min</span>
              </div>
              <div className="flex justify-between items-start gap-4 border-t border-rose-500/10 pt-4">
                <div className="space-y-0.5">
                  <strong className="text-foreground block text-sm">読解</strong>
                  <span className="text-muted-foreground block text-[11px]">Reading Comprehension (Passages & Diagrams)</span>
                </div>
                <span className="text-stone-700 dark:text-stone-300 font-bold bg-muted px-2.5 py-1 rounded border">30-60 min</span>
              </div>
              <div className="flex justify-between items-start gap-4 border-t border-rose-500/10 pt-4">
                <div className="space-y-0.5">
                  <strong className="text-foreground block text-sm">聴解 (音声一回再生のみ)</strong>
                  <span className="text-muted-foreground block text-[11px]">Listening Comprehension (Single audio run)</span>
                </div>
                <span className="text-stone-700 dark:text-stone-300 font-bold bg-muted px-2.5 py-1 rounded border">25-60 min</span>
              </div>
            </div>

            <div className="mt-8 border border-dashed border-rose-500/20 bg-rose-500/5 p-4 rounded-2xl text-xs text-rose-800 dark:text-rose-300 leading-relaxed font-sans">
              <strong>Sectional Pass Criteria:</strong> Candidates must clear both the overall pass mark AND the minimum category criteria for each section to pass the JLPT. Our CBT simulator enforces this rule in results generation.
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: JFT-Basic Preparation Section */}
      <section id="jft-basic" className="bg-muted/10 border-t border-b border-border/30 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 lg:order-2 space-y-6">
              <span className="text-xs font-bold text-amber-600 uppercase tracking-widest block font-sans">SSW Visa Qualification</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight leading-tight">
                JFT-Basic Examination Practice
              </h2>
              <p className="text-muted-foreground leading-relaxed text-sm">
                The JFT-Basic measures the Japanese language proficiency needed to communicate in daily situations in Japan. It is the core requirement to qualify for the 'Specified Skilled Worker' (Tokutei Ginou) visa. Our computer-based simulator mirrors the exact testing environment.
              </p>
              
              <div className="space-y-4 font-sans">
                {[
                  { title: "CEFR A2 Level Benchmark", desc: "Measures capacity to engage in basic conversations, read simple announcements, and handle daily transactions." },
                  { title: "Interactive CBT Audio Integration", desc: "Integrates listening questions with automated media triggers directly mirroring the official CBT tablet environment." },
                  { title: "Conversation & Expression Focus", desc: "Heavy emphasis on practical, scenario-based conversational snippets and vocabulary." }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="w-5 h-5 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 size={13} className="fill-amber-500/10" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-foreground">{item.title}</h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-2">
                <Link to="/mock-tests?examType=JFT_BASIC">
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl text-xs h-10 px-6">
                    Browse JFT Mock Exams
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 lg:order-1 bg-gradient-to-br from-amber-500/5 to-amber-500/0 border border-amber-500/10 p-8 rounded-3xl relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-44 h-44 bg-amber-500/5 rounded-full blur-xl" />
              <h3 className="text-lg font-bold text-amber-700 dark:text-amber-400 mb-6 border-b border-amber-500/10 pb-3">
                JFT-Basic 試験科目 / Test Categories
              </h3>
              
              <div className="space-y-4 font-sans text-xs">
                <div className="flex justify-between items-start gap-4">
                  <div className="space-y-0.5">
                    <strong className="text-foreground block text-sm">文字・語彙 (Letters & Vocabulary)</strong>
                    <span className="text-muted-foreground block text-[11px]">Daily nouns, kanji readings, basic verbs.</span>
                  </div>
                  <span className="text-stone-700 dark:text-stone-300 font-bold bg-muted px-2.5 py-0.5 rounded border">Section 1</span>
                </div>
                <div className="flex justify-between items-start gap-4 border-t border-amber-500/10 pt-4">
                  <div className="space-y-0.5">
                    <strong className="text-foreground block text-sm">会話・表現 (Conversation & Expression)</strong>
                    <span className="text-muted-foreground block text-[11px]">Grammar structures, interactive responses.</span>
                  </div>
                  <span className="text-stone-700 dark:text-stone-300 font-bold bg-muted px-2.5 py-0.5 rounded border">Section 2</span>
                </div>
                <div className="flex justify-between items-start gap-4 border-t border-amber-500/10 pt-4">
                  <div className="space-y-0.5">
                    <strong className="text-foreground block text-sm">読解 (Reading Comprehension)</strong>
                    <span className="text-muted-foreground block text-[11px]">Announcements, signs, instructions.</span>
                  </div>
                  <span className="text-stone-700 dark:text-stone-300 font-bold bg-muted px-2.5 py-0.5 rounded border">Section 3</span>
                </div>
                <div className="flex justify-between items-start gap-4 border-t border-amber-500/10 pt-4">
                  <div className="space-y-0.5">
                    <strong className="text-foreground block text-sm">聴解 (Listening Comprehension)</strong>
                    <span className="text-muted-foreground block text-[11px]">Short dialogues, spoken prompts.</span>
                  </div>
                  <span className="text-stone-700 dark:text-stone-300 font-bold bg-muted px-2.5 py-0.5 rounded border">Section 4</span>
                </div>
              </div>

              <div className="mt-8 border border-dashed border-amber-500/20 bg-amber-500/5 p-4 rounded-2xl text-xs text-amber-800 dark:text-amber-300 leading-relaxed font-sans">
                <strong>Test Format:</strong> 60 minutes overall, around 50 questions. Unlike the JLPT, the JFT-Basic is a single consolidated timed exam paper. Our simulator adjusts layout parameters dynamically based on the exam type.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: Why Choose Us Section */}
      <section id="why-choose-us" className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold text-primary uppercase tracking-widest block mb-2 font-sans">Why MEHUL?</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">Engineered for Academic Success</h2>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">We combine expert pedagogy with modern technology to deliver a superior learning experience.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 font-sans">
          {[
            {
              icon: Zap,
              title: "Spaced Repetition",
              desc: "Vocabulary review intervals optimized to commit kanji and grammar to your long-term memory."
            },
            {
              icon: ShieldCheck,
              title: "CBT Simulator",
              desc: "Practice with a high-fidelity simulator containing sectional locks, countdowns, and listening players."
            },
            {
              icon: Award,
              title: "Detailed Grading",
              desc: "Get digital report cards containing category pass analysis, overall score verifications, and explanations."
            },
            {
              icon: Users,
              title: "Active Community",
              desc: "Participate in channels, share study guides, collaborate on homework, and join student circles."
            }
          ].map((item, i) => (
            <KCard key={i} className="p-6 flex flex-col justify-between border border-border/80">
              <div className="space-y-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <item.icon className="text-primary" size={20} />
                </div>
                <h3 className="font-bold text-foreground text-sm tracking-wide">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            </KCard>
          ))}
        </div>
      </section>

      {/* SECTION 7: Student Success Section */}
      <section id="testimonials" className="bg-muted/15 border-t border-b border-border/30 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold text-primary uppercase tracking-widest block mb-2 font-sans">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">Loved by Successful Students</h2>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">Read how our platform helped learners pass their official qualification examinations.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 font-sans">
            {[
              {
                name: "Emily Chen",
                role: "Passed JLPT N3 in 8 months",
                text: "The timed mock tests are what made the difference. Having strict timers and only playing the audio once accurately represented the real test pressure."
              },
              {
                name: "Sabbir Ahmed",
                role: "Cleared JFT-Basic A2 for SSW Visa",
                text: "MEHUL's simulator feels exactly like the official tablet test. Thanks to the simulator practice, I felt 100% confident during my exam in Dhaka."
              },
              {
                name: "Marcus Weber",
                role: "Self-study N4 Student",
                text: "The integration between structural video lessons and matching mock tests is amazing. I can immediately practice what I just learned."
              }
            ].map((t, i) => (
              <KCard key={i} className="p-6 bg-background border border-border/80 flex flex-col justify-between shadow-xs">
                <div className="space-y-4">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} size={13} className="text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground italic leading-relaxed">
                    "{t.text}"
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-border/50 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-xs select-none">
                    {t.name[0]}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-foreground">{t.name}</h4>
                    <p className="text-[10px] text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </KCard>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: FAQ Section */}
      <section id="faq" className="bg-muted/15 border-t border-b border-border/30 py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold text-primary uppercase tracking-widest block mb-2 font-sans">Got Questions?</span>
            <h2 className="text-3xl font-extrabold text-foreground tracking-tight">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4 font-sans">
            {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div key={index} className="border border-border/80 bg-background rounded-2xl overflow-hidden transition-all shadow-xs">
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
                  >
                    <span className="text-sm font-bold text-foreground">{faq.q}</span>
                    <ChevronDown
                      size={16}
                      className={cn(
                        "text-muted-foreground shrink-0 transition-transform duration-200",
                        isOpen ? "rotate-180 text-primary" : ""
                      )}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 pt-1 text-xs text-muted-foreground leading-relaxed border-t border-border/30">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 10: Footer Section */}
      <footer className="bg-muted/30 border-t border-border/80 text-foreground py-16 font-sans">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          {/* Brand/About column */}
          <div className="md:col-span-5 space-y-4">
            <div className="flex items-center gap-2.5">
              <span className="font-heading font-black text-lg text-foreground">MEHUL</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-sm">
              MEHUL is a premium Japanese Language Education Center. We engineer digital learning tools, textbook curriculums, and mock tests designed to accelerate your route to passing official proficiency credentials.
            </p>
            <div className="flex items-center gap-3 text-muted-foreground">
              <a href="#" className="hover:text-primary transition-colors"><Globe size={18} /></a>
              <a href="#" className="hover:text-primary transition-colors"><Smartphone size={18} /></a>
            </div>
          </div>

          {/* Quick links columns */}
          <div className="md:col-span-3 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">Product Offerings</h4>
            <ul className="space-y-2 text-xs text-muted-foreground">
              <li><a href="#courses" className="hover:text-primary transition-colors">Interactive Courses</a></li>
              <li><Link to="/mock-tests" className="hover:text-primary transition-colors">Mock Test Catalog</Link></li>
              <li><a href="#jlpt" className="hover:text-primary transition-colors">JLPT Preparation</a></li>
              <li><a href="#jft-basic" className="hover:text-primary transition-colors">JFT-Basic Training</a></li>
            </ul>
          </div>

          {/* Contact column */}
          <div id="contact" className="md:col-span-4 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">Contact & Support</h4>
            <ul className="space-y-3 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <MapPin size={15} className="text-primary shrink-0 mt-0.5" />
                <span>
                  Head Office: Kazipara (Metro Pillar 267), Union Bank 4th Floor,
                  Edifice Centre, 1281 Begum Rokeya Sarani, Mirpur, Dhaka-1216
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={15} className="text-primary shrink-0" />
                <a href="tel:01804667050" className="hover:text-primary transition-colors">01804-667050</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={15} className="text-primary shrink-0" />
                <a href="mailto:info@mehul.com.bd" className="hover:text-primary transition-colors">info@mehul.com.bd</a>
              </li>
              <li className="pt-1">
                <Link to="/contact" className="text-primary font-semibold hover:underline">
                  View all 4 branches →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-8 border-t border-border/50 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
          <p>© 2026 MEHUL Japanese Language Education Center. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary transition-colors">Terms &amp; Conditions</Link>
            <Link to="/refund-policy" className="hover:text-primary transition-colors">Refund Policy</Link>
            <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookies</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
